import React from 'react'
import { useList, useUpdate } from '@refinedev/core'
import { App, Switch } from 'antd'
import { brand } from '../../theme/brand'
import { Pill, SectionCard } from '../../theme/listkit'
import { Sparkles } from '../../components/icons'

/** The one row this switch owns. The app, the insert policy, community-propose/-dispatch and send-push all read it. */
export const SUGGESTION_BOX_FLAG = 'suggestion_box_enabled'

interface FeatureFlagRow {
  key: string
  enabled: boolean
}

/**
 * The Suggestion Box's on/off switch, at the top of its triage queue.
 *
 * It writes `feature_flags.suggestion_box_enabled`, which the app reads over
 * Realtime: within seconds of switching off, the Home card disappears and an
 * open Suggestion Box screen changes to its "Closed for now" card, with no
 * app update and no relaunch. The server stops taking new suggestions at the
 * same moment (the insert policy and trigger read the same row), and
 * community-dispatch and send-push hold or refuse a push that would open the
 * box. Nothing already sent is touched; it stays in the queue below.
 *
 * Switching OFF is one tap with no dialog: a brake should never be gated (the
 * community-manager Controls rule). Switching ON asks first, because it puts
 * the card in front of every member at once.
 *
 * The row is found by key in the list rather than trusted from the filter, so
 * a data provider that ignores filters still reads the right flag. No row at
 * all means the Suggestion Box migration is missing; the switch is disabled
 * rather than pretending the box is closed.
 */
export function SuggestionBoxSwitch() {
  const { message, modal } = App.useApp()
  const { result, query } = useList<FeatureFlagRow>({
    resource: 'feature_flags',
    filters: [{ field: 'key', operator: 'eq', value: SUGGESTION_BOX_FLAG }],
    pagination: { pageSize: 1 },
  })
  const { mutate: update, mutation } = useUpdate()

  const row = (result?.data ?? []).find((r) => r.key === SUGGESTION_BOX_FLAG) ?? null
  const loading = query.isLoading
  const missing = !loading && row === null
  const open = row?.enabled === true
  const saving = mutation.isPending

  function write(enabled: boolean) {
    update(
      {
        resource: 'feature_flags',
        id: SUGGESTION_BOX_FLAG,
        values: { enabled },
        meta: { idColumnName: 'key' },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () =>
          message.success(
            enabled
              ? 'The Suggestion Box is open. Members see it within seconds.'
              : 'The Suggestion Box is closed. It leaves members’ screens within seconds.',
          ),
        onError: (e) => message.error(`Couldn’t change the Suggestion Box: ${e?.message ?? 'unknown error'}`),
      },
    )
  }

  function onChange(checked: boolean) {
    if (!checked) {
      write(false)
      return
    }
    modal.confirm({
      title: 'Open the Suggestion Box?',
      content: 'Every member will see the Suggestion Box card on Home within seconds and can send ideas.',
      okText: 'Open it',
      cancelText: 'Cancel',
      onOk: () => write(true),
    })
  }

  return (
    <SectionCard
      title="Suggestion Box in the app"
      sub="Changes reach the app within seconds"
      style={{ maxWidth: 720, marginBottom: 16 }}
    >
      <div
        data-testid="suggestion-box-switch-row"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
          <div style={{ marginTop: 2, flexShrink: 0 }}>
            <Sparkles size={18} color={brand.gold} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <span style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 14, color: brand.ink }}>
                Accepting suggestions
              </span>
              {!loading && !missing ? (
                <Pill tone={open ? 'green' : 'neutral'}>{open ? 'Open' : 'Closed'}</Pill>
              ) : null}
            </div>
            <div
              data-testid="suggestion-box-switch-help"
              style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12, color: brand.sub, lineHeight: 1.5 }}
            >
              {missing
                ? `The ${SUGGESTION_BOX_FLAG} flag is missing, so the Suggestion Box can't be switched here. Apply the Suggestion Box migration first.`
                : open
                  ? 'Members see the Suggestion Box on Home and can send ideas. Switch off to hide it right away.'
                  : 'Hidden from members: no Home card, and the screen says it’s closed for now. Suggestions already sent stay below.'}
            </div>
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Switch
            data-testid="suggestion-box-switch"
            aria-label="Accepting suggestions"
            checked={open}
            disabled={loading || missing}
            loading={saving}
            onChange={onChange}
            style={{ background: open ? brand.gold : undefined }}
          />
        </div>
      </div>
    </SectionCard>
  )
}
